import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import UploadSection from './UploadSection';
import AnalysisResults from './AnalysisResults';
// import HistoryModal from './HistoryModal';
import DownloadModal from './DownloadModal';
import './styles.scss';
import apiConfig from '../../../credentials/apiConfig';
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Loader from '../../../../components/Loader';
import { FiUpload, FiX, FiSearch, FiEye, FiTrash2 } from 'react-icons/fi';

const DocumentSummary = ({ clientId }) => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [analysisGenerated, setAnalysisGenerated] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  // const [currentVersion, setCurrentVersion] = useState(5);
  const [analysisData, setAnalysisData] = useState(null);
  const [flaggedPointsData, setFlaggedPointsData] = useState(null);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // Track search term
  const [documentBeingAnalyzed, setDocumentBeingAnalyzed] = useState(null);

  
  const [loading, setLoading] = useState(true);


// Fetch document summary with search capabilities
const fetchDocumentSummary = async (searchString = "") => {
  try {
    setLoading(true);
    const authToken = localStorage.getItem("authToken");

    // Send the searchString parameter in the payload
    const response = await axios.post(
      `${apiConfig.apiBaseURL}/getDocumentSummary`,
      { searchString: searchString || "" }, // Use empty string if searchString is falsy
      {
        headers: {
          "x-api-key": apiConfig["x-api-key"],
          "Authorization": `Bearer ${authToken}`,
          "Content-Type": "application/json"
        }
      }
    );

    if (response.data.success) {
      // NEW: Direct processing of the flat array structure
      const allDocs = response.data.data || [];
      
      // Process each document to ensure consistent structure
      const processedDocs = allDocs.map(doc => ({
        ...doc,
        clientId: doc.clientId,
        fileType: doc.type || 'boardMeetingMinutes', // Use 'type' field from new structure
        clientName: doc.clientName || ''
      }));

      setUploadedDocuments(processedDocs);
      setFiles([]);

      // Store search term in state
      setSearchTerm(searchString || "");

      // Auto-select logic: always select the latest document
      if (processedDocs.length === 0) {
        setSelectedDocument(null);
        setAnalysisGenerated(false);
        setAnalysisData(null);
        setFlaggedPointsData(null);
        
        if (searchString) {
          console.log("Search with no results, keeping previous analysis data");
        }
      } 
      else if (processedDocs.length > 0 && !searchString) {
        // Sort by upload/update date and select the latest document
        
        const latestDocument = processedDocs[0];
        setSelectedDocument(latestDocument);
        
        // Auto-display summary if available
        if (latestDocument?.summary && latestDocument?.flaggedPoints) {
          setAnalysisGenerated(true);
          const rawMarkdown = latestDocument.summary;
          const flaggedPointsText = latestDocument.flaggedPoints;

          setAnalysisData({
            title: getDocumentTitle(latestDocument),
            markdown: rawMarkdown,
            rawFlaggedPoints: flaggedPointsText
          });

          const riskCategories = parseRiskCategories(flaggedPointsText);
          setFlaggedPointsData(riskCategories);
        } else {
          setAnalysisGenerated(false);
        }
      } else if (processedDocs.length > 1 && searchString) {
        // During search, don't auto-select, let user choose
        setAnalysisGenerated(false);
      }
    } else {
      toast.error(response.data.message || "Failed to fetch document summary");
      
      // Reset states on API error
      setSelectedDocument(null);
      setAnalysisGenerated(false);
      setAnalysisData(null);
      setFlaggedPointsData(null);
    }
  } catch (err) {
    console.error("Error fetching document summary:", err);

    // Reset states on exception
    setSelectedDocument(null);
    setAnalysisGenerated(false);
    setAnalysisData(null);
    setFlaggedPointsData(null);

    if (err.response) {
      console.error("Response data:", err.response.data);
      toast.error(`Error fetching document summary: ${err.response.data?.message || err.response.statusText}`);
    } else if (err.request) {
      console.error("No response received:", err.request);
      toast.error("Server did not respond. Please check your network connection.");
    } else {
      toast.error(`Error fetching document summary: ${err.message}`);
    }
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  fetchDocumentSummary(); // initial load
}, []);

  // Helper function to get a nice title for the document
  const getDocumentTitle = (document) => {
    if (!document) return "Document Summary";

    const fileName = document.fileUrl ? document.fileUrl.split('/').pop().split('?')[0] : "Document";
    const cleanFileName = decodeURIComponent(fileName).replace(/\.[^/.]+$/, ""); // Remove file extension

    return cleanFileName;
  };

 // Helper function to parse risk categories from flagged points text
const parseRiskCategories = (flaggedPointsText) => {
  
  if (!flaggedPointsText) return [];

  // Handle case where flaggedPoints is already an object
  if (typeof flaggedPointsText === 'object' && !Array.isArray(flaggedPointsText)) {
    return flaggedPointsText;
  }

  const riskCategories = [];
  let currentCategory = null;

  // Ensure we're working with string data
  const textToProcess = typeof flaggedPointsText === 'string' 
    ? flaggedPointsText 
    : Array.isArray(flaggedPointsText) 
      ? flaggedPointsText.join('\n') 
      : JSON.stringify(flaggedPointsText);

  // Split by lines and process
  const lines = textToProcess.split('\n').map(line => line.trim()).filter(Boolean);
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Detect H2 category headers (## Category Name)
    if (line.startsWith('## ')) {
      // Save previous category if it exists
      if (currentCategory && currentCategory.items.length > 0) {
        riskCategories.push(currentCategory);
      }
      
      // Start new category
      currentCategory = {
        category: line.replace('## ', '').trim(),
        items: []
      };
    }
    // Detect numbered list items (1. Item text, 2. Item text, etc.)
    else if (line.match(/^\d+\.\s+\*\*.*?\*\*:/)) {
      if (currentCategory) {
        // Extract the item text and description
        const match = line.match(/^\d+\.\s+\*\*(.*?)\*\*:\s*(.*)/);
        if (match) {
          const itemTitle = match[1].trim();
          const itemDescription = match[2].trim();
          currentCategory.items.push(`**${itemTitle}**: ${itemDescription}`);
        }
      }
    }
    // Handle items that might span multiple lines or have different formatting
    else if (currentCategory && line.length > 10 && !line.startsWith('#')) {
      // Skip intro lines and headers, only add substantive content
      if (!line.includes("I'll analyze") && 
          !line.includes("Red Flags in") && 
          !line.includes("potential red flags")) {
        
        // Check if this line is a continuation of a numbered item
        if (!line.match(/^\d+\./)) {
          // This might be a continuation of the previous item
          if (currentCategory.items.length > 0) {
            // Append to the last item
            const lastIndex = currentCategory.items.length - 1;
            currentCategory.items[lastIndex] += ` ${line}`;
          }
        }
      }
    }
  }

  // Add the last category if it exists
  if (currentCategory && currentCategory.items.length > 0) {
    riskCategories.push(currentCategory);
  }

  // If no structured categories were found, try a more flexible approach
  if (riskCategories.length === 0 && textToProcess.trim().length > 0) {
    // Look for any ## headers and content after them
    const sections = textToProcess.split(/(?=##\s)/);
    
    sections.forEach(section => {
      const sectionLines = section.split('\n').filter(line => line.trim());
      if (sectionLines.length === 0) return;
      
      const headerLine = sectionLines.find(line => line.startsWith('##'));
      if (headerLine) {
        const categoryName = headerLine.replace('##', '').trim();
        const items = [];
        
        // Extract numbered items from this section
        sectionLines.forEach(line => {
          const numberedMatch = line.match(/^\d+\.\s+\*\*(.*?)\*\*:\s*(.*)/);
          if (numberedMatch) {
            items.push(`**${numberedMatch[1].trim()}**: ${numberedMatch[2].trim()}`);
          }
        });
        
        if (items.length > 0) {
          riskCategories.push({
            category: categoryName,
            items: items
          });
        }
      }
    });
  }

  // Final fallback - if still no categories, create a single one
  if (riskCategories.length === 0 && textToProcess.trim().length > 0) {
    riskCategories.push({
      category: "Risk Analysis",
      items: [textToProcess.trim()]
    });
  }

  return riskCategories;
};
  const addFiles = (newFiles) => {
    setFiles(prevFiles => [...prevFiles, ...newFiles]);
  };

  const removeFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const generateAnalysis = async (document) => {
    if (document) {
      // Start polling for this document
      setDocumentBeingAnalyzed(document);
    }
    setAnalysisGenerated(true);
  };

  // const historyData = [
  //   {
  //     version: 3,
  //     date: "30/01/2025",
  //     time: "18:45:56",
  //     fileName: "Financial_Report_2024.pdf"
  //   },
  //   {
  //     version: 2,
  //     date: "29/01/2025",
  //     time: "10:23:17",
  //     fileName: "Nexora_innovations_2024.pdf",
  //     hasSubItems: true
  //   },
  //   {
  //     version: 1,
  //     date: "27/01/2025",
  //     time: "2:21:25",
  //     fileName: "nexora_innovations_Financial_Report_2024.pdf"
  //   }
  // ];

  if (loading) return <Loader />;

  return (
    <>
      <Container fluid className="document-summary-container my-5">
        <div className='all-blocks'>
          <Row className="m-0 h-100">
            <Col md={12} lg={4} className="p-4 doc-section">
             <UploadSection
  selectedDocument={selectedDocument}
  setSelectedDocument={setSelectedDocument}
  files={files}
  addFiles={addFiles}
  removeFile={removeFile}
  generateAnalysis={generateAnalysis}
  uploadedDocuments={uploadedDocuments}
  setUploadedDocuments={setUploadedDocuments}
  updateAnalysisData={(rawMarkdown, flaggedPointsText) => {
    
    if (!rawMarkdown) {
      setAnalysisData({
        markdown: ""
      });
      setFlaggedPointsData([]);
      return;
    }

    // Set the analysis data
    setAnalysisData({
      title: selectedDocument ? getDocumentTitle(selectedDocument) : "Document Analysis",
      markdown: rawMarkdown,
      rawFlaggedPoints: flaggedPointsText  // Store raw flagged points
    });

    // Process flagged points
    if (flaggedPointsText) {
      const riskCategories = parseRiskCategories(flaggedPointsText);
      setFlaggedPointsData(riskCategories);
    } else {
      setFlaggedPointsData([]);
    }
  }}
  setAnalysisGenerated={setAnalysisGenerated} 
  onRefreshDocumentList={fetchDocumentSummary}
/>
             
            </Col>

           <Col md={12} lg={8} className="p-4 review-section">
  {!analysisGenerated ? (
    <div className="d-flex align-items-center justify-content-center h-100 text-muted">
      {uploadedDocuments.length === 0 && !searchTerm ? (
        "Select the 'Respective PDF, DOC, or DOCX' to view the summary."
      ) : uploadedDocuments.length === 1 ? (
        "Generating summary for your document..."
      ) : (
        "Select a document to view its summary."
      )}
    </div>
  ) : (
    <>
      {/* This is the key improvement - we check if we're searching with no results */}
      {searchTerm && uploadedDocuments.length === 0 && selectedDocument ? (
        <div className="position-relative">
          {/* Add a semi-transparent overlay with search info */}
          <div 
            className="position-absolute w-100 h-100" 
            style={{ 
              background: 'rgba(255,255,255,0.7)', 
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '2rem'
            }}
          >
            <div className="bg-white p-4 rounded shadow-sm text-center" style={{ maxWidth: '80%' }}>
              <FiSearch size={32} className="text-primary mb-3" />
              <h5>No Documents Match Your Search</h5>
              <p className="text-muted">
                Your search for "{searchTerm}" returned no results.
                <br />
                The previously selected document is shown behind this message.
              </p>
              <Button 
                variant="primary" 
                onClick={() => {
                  setSearchTerm('');
                  fetchDocumentSummary();
                }}
              >
                Clear Search
              </Button>
            </div>
          </div>
          
          {/* Show the analysis results behind the overlay */}
          <AnalysisResults
            analysisData={analysisData}
            flaggedPointsData={flaggedPointsData}
            onViewHistory={() => setShowHistoryModal(true)}
            selectedDocument={selectedDocument}
            clientId={clientId}
            onDownload={() => setShowDownloadModal(true)}
          />
        </div>
      ) : (
        // Normal display when not in "no results" state
        <AnalysisResults
          analysisData={analysisData}
          flaggedPointsData={flaggedPointsData}
          onViewHistory={() => setShowHistoryModal(true)}
          selectedDocument={selectedDocument}
          clientId={clientId}
          onDownload={() => setShowDownloadModal(true)}
        />
      )}
    </>
  )}
</Col>
          </Row>
        </div>

        {/* <HistoryModal
          show={showHistoryModal}
          onHide={() => setShowHistoryModal(false)}
          historyData={historyData}
        /> */}

        <DownloadModal
  show={showDownloadModal}
  onHide={() => setShowDownloadModal(false)}
  selectedDocument={selectedDocument}
  clientId={clientId || selectedDocument?.clientId || ""}
/>
      </Container>
    </>
  );
};

export default DocumentSummary;